// ApiService - Generic wrapper for all API calls with proper typing
import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import BaseService from './BaseService'

export interface ApiResponse<T = any> {
  data: T
  message?: string
  success: boolean
  statusCode?: number
}

export interface ApiError {
  message: string
  statusCode?: number
  errors?: Record<string, string[]>
}

export interface FetchDataConfig<TRequest = any> extends Omit<AxiosRequestConfig, 'data'> {
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  data?: TRequest
  params?: Record<string, any>
  headers?: Record<string, string>
}

class ApiService {
  async fetchData<TResponse = any, TRequest = any>(
    config: FetchDataConfig<TRequest>
  ): Promise<ApiResponse<TResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<TResponse>> = await BaseService({
        ...config,
        method: config.method || 'GET',
      })

      return response.data
    } catch (error) {
      return this.handleError(error as AxiosError<ApiResponse>)
    }
  }

  private handleError(error: AxiosError<ApiResponse>): never {
    const apiError: ApiError = {
      message: error.response?.data?.message || error.message || 'An unexpected error occurred',
      statusCode: error.response?.status,
      errors: error.response?.data?.data as Record<string, string[]> | undefined,
    }

    console.error('[ApiService Error]:', apiError)

    throw apiError
  }

  async get<TResponse = any>(url: string, params?: Record<string, any>): Promise<ApiResponse<TResponse>> {
    return this.fetchData<TResponse>({ url, method: 'GET', params })
  }

  async post<TResponse = any, TRequest = any>(
    url: string,
    data?: TRequest
  ): Promise<ApiResponse<TResponse>> {
    return this.fetchData<TResponse, TRequest>({ url, method: 'POST', data })
  }

  async put<TResponse = any, TRequest = any>(
    url: string,
    data?: TRequest
  ): Promise<ApiResponse<TResponse>> {
    return this.fetchData<TResponse, TRequest>({ url, method: 'PUT', data })
  }

  async patch<TResponse = any, TRequest = any>(
    url: string,
    data?: TRequest
  ): Promise<ApiResponse<TResponse>> {
    return this.fetchData<TResponse, TRequest>({ url, method: 'PATCH', data })
  }

  async delete<TResponse = any>(url: string): Promise<ApiResponse<TResponse>> {
    return this.fetchData<TResponse>({ url, method: 'DELETE' })
  }
}

const apiServiceInstance = new ApiService()
export default apiServiceInstance
